import { Injectable, inject } from '@angular/core';

import { STORAGE_KEY } from '../constants/storage.constants';
import { LocalStorageService } from './local-storage.service';

export interface EventWizardLocationReturn {
  section: string | null;
  returnUrl?: string | null;
  editEventId?: string | null;
}

export const EVENT_WIZARD_RETURN_TO_PARAM = 'returnTo';
export const EVENT_WIZARD_RETURN_TO_EVENT = 'event';
export const EVENT_WIZARD_RETURN_PARAM = 'wizardReturn';
export const EVENT_WIZARD_LOCATION_SAVED_PARAM = 'locationSaved';

const DEFAULT_RETURN_URL = '/events/create';

@Injectable({ providedIn: 'root' })
export class EventWizardReturnService {
  private readonly storage = inject(LocalStorageService);

  public remember(
    section: string | null,
    returnUrl: string | null = null,
    editEventId: string | null = null,
  ): void {
    this.storage.setItem<EventWizardLocationReturn>(
      STORAGE_KEY.eventWizardLocationReturn,
      { section, returnUrl, editEventId },
    );
  }

  public returnUrl(): string {
    return this.read()?.returnUrl ?? DEFAULT_RETURN_URL;
  }

  public read(): EventWizardLocationReturn | null {
    return this.storage.getItem<EventWizardLocationReturn>(
      STORAGE_KEY.eventWizardLocationReturn,
    );
  }

  public isPending(): boolean {
    const pending = this.read();

    if (pending === null) {
      return false;
    }

    if (!this.hasDraftFor(pending)) {
      this.forget();
      return false;
    }

    return true;
  }

  public forget(): void {
    this.storage.removeItem(STORAGE_KEY.eventWizardLocationReturn);
  }

  private hasDraftFor(pending: EventWizardLocationReturn): boolean {
    if (!pending.editEventId) {
      return this.storage.getItem<unknown>(STORAGE_KEY.eventDraft) !== null;
    }

    const editDrafts = this.storage.getItem<Record<string, unknown>>(
      STORAGE_KEY.eventEditDrafts,
    );

    return editDrafts !== null && pending.editEventId in editDrafts;
  }
}
